import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Link as LinkIcon,
    Image as ImageIcon,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Undo2,
    Redo2,
    Code,
    Minus,
} from 'lucide-react';
import { uploadImage } from '@/lib/uploader';
import { cn } from '@/lib/utils';

/**
 * RichTextEditor — a WYSIWYG editor for CMS HTML content, backed by TipTap.
 *
 * Props:
 * - value: string (HTML)
 * - onChange: (html) => void
 * - placeholder?: string
 * - testId?: string
 */
const ToolbarBtn = ({ onClick, isActive, disabled, title, testId, children }) => (
    <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClick}
        disabled={disabled}
        className={cn(
            'h-8 w-8 shrink-0',
            isActive
                ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                : 'text-foreground/70 hover:text-foreground'
        )}
        title={title}
        data-testid={testId}
    >
        {children}
    </Button>
);

export const RichTextEditor = ({
    value = '',
    onChange,
    placeholder = 'Start writing…',
    testId,
    className = '',
    minHeight = 220,
}) => {
    const fileInputRef = useRef(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3, 4] },
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
            }),
            Image.configure({
                inline: false,
                allowBase64: false,
                HTMLAttributes: { class: 'rounded-lg my-3 max-w-full h-auto' },
            }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || '',
        editorProps: {
            attributes: {
                class: cn(
                    'prose prose-sm sm:prose max-w-none focus:outline-none px-4 py-3',
                    '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
                    '[&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold',
                    '[&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold',
                    '[&_a]:text-[hsl(var(--primary))] [&_a]:underline',
                    '[&_blockquote]:border-l-4 [&_blockquote]:border-[hsl(var(--accent))] [&_blockquote]:pl-4 [&_blockquote]:italic',
                    '[&_p]:leading-relaxed'
                ),
                'data-testid': testId ? `${testId}-content` : undefined,
                style: `min-height:${minHeight}px`,
            },
        },
        onUpdate: ({ editor: ed }) => {
            const html = ed.getHTML();
            // Convert empty editor "<p></p>" to '' for cleaner storage
            onChange && onChange(html === '<p></p>' ? '' : html);
        },
    });

    // Keep external value → editor content in sync
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if ((value || '') !== (current === '<p></p>' ? '' : current)) {
            editor.commands.setContent(value || '', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) {
        return (
            <div
                className={cn(
                    'rounded-lg border border-border bg-card',
                    className
                )}
                style={{ minHeight: minHeight + 60 }}
            />
        );
    }

    const promptLink = () => {
        const previous = editor.getAttributes('link').href;
        const url = window.prompt('URL', previous || 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: url })
            .run();
    };

    const pickImage = () => fileInputRef.current?.click();

    const onImageSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // reset input
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please choose an image');
            return;
        }
        try {
            const res = await uploadImage(file, 'inline');
            editor.chain().focus().setImage({ src: res.url, alt: file.name }).run();
            toast.success('Image inserted');
        } catch (err) {
            const msg =
                err?.response?.data?.detail || err?.message || 'Image upload failed';
            toast.error(msg);
        }
    };

    return (
        <div
            className={cn(
                'rounded-lg border border-input bg-card',
                'focus-within:ring-2 focus-within:ring-[hsl(var(--ring))]/40',
                className
            )}
            data-testid={testId}
        >
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5 bg-[hsl(var(--secondary))]/40">
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    title="Bold"
                    testId={testId ? `${testId}-btn-bold` : undefined}
                >
                    <Bold className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    title="Italic"
                    testId={testId ? `${testId}-btn-italic` : undefined}
                >
                    <Italic className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    title="Underline"
                    testId={testId ? `${testId}-btn-underline` : undefined}
                >
                    <UnderlineIcon className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={editor.isActive('strike')}
                    title="Strikethrough"
                    testId={testId ? `${testId}-btn-strike` : undefined}
                >
                    <Strikethrough className="h-3.5 w-3.5" />
                </ToolbarBtn>

                <Separator orientation="vertical" className="mx-1 h-5" />

                <ToolbarBtn
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    isActive={editor.isActive('heading', { level: 2 })}
                    title="Heading 2"
                    testId={testId ? `${testId}-btn-h2` : undefined}
                >
                    <Heading2 className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 3 }).run()
                    }
                    isActive={editor.isActive('heading', { level: 3 })}
                    title="Heading 3"
                    testId={testId ? `${testId}-btn-h3` : undefined}
                >
                    <Heading3 className="h-3.5 w-3.5" />
                </ToolbarBtn>

                <Separator orientation="vertical" className="mx-1 h-5" />

                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title="Bulleted list"
                    testId={testId ? `${testId}-btn-ul` : undefined}
                >
                    <List className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title="Numbered list"
                    testId={testId ? `${testId}-btn-ol` : undefined}
                >
                    <ListOrdered className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive('blockquote')}
                    title="Quote"
                    testId={testId ? `${testId}-btn-quote` : undefined}
                >
                    <Quote className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    isActive={editor.isActive('code')}
                    title="Inline code"
                    testId={testId ? `${testId}-btn-code` : undefined}
                >
                    <Code className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    title="Horizontal rule"
                    testId={testId ? `${testId}-btn-hr` : undefined}
                >
                    <Minus className="h-3.5 w-3.5" />
                </ToolbarBtn>

                <Separator orientation="vertical" className="mx-1 h-5" />

                <ToolbarBtn
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    title="Align left"
                    testId={testId ? `${testId}-btn-align-left` : undefined}
                >
                    <AlignLeft className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    isActive={editor.isActive({ textAlign: 'center' })}
                    title="Align center"
                    testId={testId ? `${testId}-btn-align-center` : undefined}
                >
                    <AlignCenter className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    title="Align right"
                    testId={testId ? `${testId}-btn-align-right` : undefined}
                >
                    <AlignRight className="h-3.5 w-3.5" />
                </ToolbarBtn>

                <Separator orientation="vertical" className="mx-1 h-5" />

                <ToolbarBtn
                    onClick={promptLink}
                    isActive={editor.isActive('link')}
                    title="Insert / edit link"
                    testId={testId ? `${testId}-btn-link` : undefined}
                >
                    <LinkIcon className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={pickImage}
                    title="Insert image (uploaded to CDN)"
                    testId={testId ? `${testId}-btn-image` : undefined}
                >
                    <ImageIcon className="h-3.5 w-3.5" />
                </ToolbarBtn>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={onImageSelected}
                />

                <div className="ml-auto flex items-center gap-0.5">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        title="Undo"
                        testId={testId ? `${testId}-btn-undo` : undefined}
                    >
                        <Undo2 className="h-3.5 w-3.5" />
                    </ToolbarBtn>
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        title="Redo"
                        testId={testId ? `${testId}-btn-redo` : undefined}
                    >
                        <Redo2 className="h-3.5 w-3.5" />
                    </ToolbarBtn>
                </div>
            </div>

            <EditorContent editor={editor} />
        </div>
    );
};

export default RichTextEditor;
